import * as THREE from 'three';
import { DEEP_CRYPT } from '@somnhold/content';
import { hexToWorld, type DeepCrypt, type DungeonLayout } from '@somnhold/sim';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { CRYPT, CRYPT_FRAG, VEIN_PARS, veinTexture } from './ascuas.ts';
import { simToWorld, WORLD_PER_HEX } from './coords.ts';
import type { CryptBuild } from './crypt.ts';
import type { GlowLayer } from './glow.ts';
import type { Vfx } from './vfx.ts';

/*
 * The deep crypt (level III): three floors in one descent, laid far apart on one map.
 *
 * - Each floor dresses differently on the same merged crypt mesh (its triangles grouped by floor, `aFloor`;
 *   only the floor being fought is drawn, and the shader knows which by a uniform): 0 the plain crypt's slate; 1 the crypt in embers (basalt, fire in the grout, the same shader
 *   as level II); 2 obsidian, black glass flagstones cut in facets with violet seams and a cold violet
 *   glow at the foot of the walls. Torches, rune circles and the murk follow the floor.
 * - The room lights: only one floor's worth exists (lights are expensive on phones); they move to the
 *   floor being fought.
 * - The descent: once a guardian falls, a stairwell going down (a dark opening, steps into a red glow)
 *   and a way out (a shaft of pale light, a rope ladder going up), each with a ring on the floor that
 *   fills while the whole party stands on it. Going down cuts to black for a moment.
 */

const FLOOR_FRAG = /* glsl */ `
{
  vec2 p = vAscW.xz;
  vec3 wn = normalize(cross(dFdx(vAscW), dFdy(vAscW)));
  float up = smoothstep(0.75, 0.95, abs(wn.y));
  float floorY = 1.0 - smoothstep(0.08, 0.2, vAscW.y);
  float flr = up * floorY;
  vec4 a = texture2D(tVein, p * 0.16);
  vec4 b = texture2D(tVein, p * 0.37 + vec2(0.37, 0.61));
  float e = max(1.0 - smoothstep(0.0, 0.022, a.r), (1.0 - smoothstep(0.0, 0.016, b.r)) * 0.5) * flr;
  diffuseColor.rgb *= mix(1.0, 0.6 + 0.85 * a.g, flr);
  diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.34, 0.32, 0.44), e * 0.4);
  float tw = pow(max(0.0, sin(uAscTime * (0.3 + a.g * 0.5) + a.g * 47.0 + b.g * 13.0)), 28.0);
  totalEmissiveRadiance += mix(vec3(0.62, 0.42, 1.0), vec3(1.0, 0.31, 0.23), step(0.88, b.g)) * e * (0.05 + 0.75 * tw);
  float grout = (1.0 - smoothstep(-0.03, -0.02, vAscW.y)) * flr;
  totalEmissiveRadiance += vec3(0.16, 0.07, 0.34) * grout * 0.1;
  float wall = (1.0 - up) * (1.0 - smoothstep(-0.1, 0.3, vAscW.y)) * step(-0.35, vAscW.y);
  totalEmissiveRadiance += vec3(0.06, 0.025, 0.12) * wall;
}`;

const FLOOR_NORMAL = /* glsl */ `
if (uFloor > 1.5) {
  vec4 fa = texture2D(tVein, vAscW.xz * 0.16);
  vec3 tilt = vec3(fa.g - 0.5, 0.0, fract(fa.g * 7.31 + 0.13) - 0.5) * 0.7;
  normal = normalize(normal + (viewMatrix * vec4(tilt, 0.0)).xyz);
}`;

const deepMaterial = (src: THREE.MeshStandardMaterial, uniforms: Record<string, THREE.IUniform>): THREE.MeshStandardMaterial => {
  const m = src.clone();
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vAscW;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvAscW = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      // One floor is drawn at a time (byFloor), so which one is a uniform: no branch per pixel.
      .replace('#include <common>', `#include <common>\n${VEIN_PARS}\nuniform float uFloor;`)
      .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\nif (uFloor > 1.5) roughnessFactor = 0.3;')
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>\n${FLOOR_NORMAL}`)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>\nif (uFloor > 0.5 && uFloor < 1.5) ${CRYPT_FRAG}\nelse if (uFloor > 1.5) ${FLOOR_FRAG}`);
  };
  m.customProgramCacheKey = () => 'somnhold-deep-crypt';
  m.needsUpdate = true;
  return m;
};

/**
 * Puts each floor's triangles together, in the order they had, and returns each floor's [first vertex, count], so
 * only the floor being fought is drawn (a draw range). The shader used to discard the other floors: the whole mesh's
 * vertices every frame and, on a phone's GPU, no early depth test under anything it covers (client-perf, 2026-10-30:
 * the deep crypt at 8–12 fps on an Adreno 610). Same order within a floor, so the same pixels win the depth test.
 */
const byFloor = (geo: THREE.BufferGeometry, fl: THREE.BufferAttribute): [number, number][] => {
  const tris = fl.count / 3;
  const order: number[] = [];
  const ranges: [number, number][] = [];
  for (let f = 0; f < 3; f++) {
    const first = order.length;
    for (let t = 0; t < tris; t++) if (Math.round(fl.getX(t * 3)) === f) order.push(t);
    ranges.push([first * 3, (order.length - first) * 3]);
  }
  for (const name of Object.keys(geo.attributes)) {
    const a = geo.getAttribute(name) as THREE.BufferAttribute;
    const src = a.array;
    const per = a.itemSize * 3;
    const dst = new (src.constructor as Float32ArrayConstructor)(src.length);
    order.forEach((t, i) => dst.set(src.subarray(t * per, t * per + per), i * per));
    geo.setAttribute(name, new THREE.BufferAttribute(dst, a.itemSize, a.normalized));
  }
  return ranges;
};

const lum = (r: number, g: number, b: number): number => 0.2126 * r + 0.7152 * g + 0.0722 * b;
const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

const GLASS = {
  floorDark: new THREE.Color('#0b0a10'),
  floorLight: new THREE.Color('#2e2b39'),
  grout: new THREE.Color('#040307'),
  wallDark: new THREE.Color('#07060a'),
  wallLight: new THREE.Color('#25222e'),
};

/** Each floor's murk and moonlight: stone, embers, obsidian. */
const FLOOR_SKY = [
  { bg: '#07080a', fog: '#07080a', hemi: ['#5a6478', '#0a0a0e'], moon: '#8a9cc8', room: '#ff9a4a', hall: '#9b4dff' },
  { bg: '#0a0404', fog: '#120605', hemi: ['#8a6a66', '#2a0c06'], moon: '#b08a86', room: '#ff8a5a', hall: '#ff4a2a' },
  { bg: '#07060c', fog: '#0e0c16', hemi: ['#7a78a0', '#120a1e'], moon: '#a69cd8', room: '#b9a6ff', hall: '#c9b8ff' },
] as const;

/* ---------- the stairwell, the way out and their rings ---------- */

const LOCAL_VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

/** The stairwell seen from above: a dark slot, steps going down into a red glow, a stone lip round it. */
const STAIRS_FRAG = /* glsl */ `
uniform float uTime;
varying vec2 vUv;
void main() {
  vec2 q = vUv * 2.0 - 1.0;
  float u = vUv.y;
  float inner = step(abs(q.x), 0.78) * step(abs(q.y), 0.86);
  float steps = 7.0;
  float k = floor(u * steps);
  float f = fract(u * steps);
  float depth = k / steps;
  float lip = (1.0 - smoothstep(0.0, 0.16, f)) * (1.0 - depth * 0.85);
  vec3 stoneC = vec3(0.1, 0.1, 0.11) * (1.0 - depth) * (0.75 + 0.25 * (1.0 - f));
  float pulse = 0.8 + 0.2 * sin(uTime * 1.7);
  vec3 red = vec3(1.0, 0.22, 0.12) * pow(depth, 2.2) * 0.9 * pulse;
  vec3 col = stoneC + vec3(0.22, 0.21, 0.24) * lip + red;
  col *= 1.0 - 0.55 * smoothstep(0.5, 0.78, abs(q.x));
  vec3 rim = vec3(0.26, 0.26, 0.29);
  col = mix(rim, col, inner);
  float a = step(abs(q.x), 1.0) * step(abs(q.y), 1.0);
  gl_FragColor = vec4(col, a);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

/** A ring on the floor that fills (clockwise from the top) while the whole party stands in it. */
const RING_FRAG = /* glsl */ `
uniform float uTime;
uniform float uFill;
uniform vec3 uColor;
varying vec2 vUv;
void main() {
  vec2 q = vUv * 2.0 - 1.0;
  float d = length(q);
  float ang = atan(q.x, q.y) / 6.2831 + 0.5;
  float band = smoothstep(0.84, 0.87, d) * (1.0 - smoothstep(0.95, 0.98, d));
  float filled = step(ang, uFill);
  float dash = step(0.5, fract(ang * 36.0));
  float pulse = 0.75 + 0.25 * sin(uTime * 4.0);
  float line = exp(-pow((d - 0.86) / 0.012, 2.0));
  float a = band * mix(0.22 * dash, 0.95, filled) + line * 0.5 * pulse;
  float pool = (1.0 - smoothstep(0.0, 0.86, d)) * 0.1 * (0.6 + uFill);
  vec3 col = uColor * (band * mix(0.5, 1.4, filled) + line * pulse + pool);
  gl_FragColor = vec4(col, clamp(a + pool, 0.0, 1.0));
}`;

/** The shaft of pale light over the way out: brightest at the floor, fading up into the dark. */
const SHAFT_FRAG = /* glsl */ `
uniform float uTime;
varying vec2 vUv;
void main() {
  float y = vUv.y;
  float edge = sin(vUv.x * 3.14159 * 2.0) * 0.5 + 0.5;
  float a = (1.0 - smoothstep(0.0, 1.0, y)) * (0.35 + 0.3 * sin(vUv.x * 40.0 + uTime * 0.6) * 0.3) * (0.7 + 0.3 * edge);
  gl_FragColor = vec4(vec3(0.85, 0.9, 1.0) * a, a);
}`;

const FADE_VERT = /* glsl */ `
void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }`;
const FADE_FRAG = /* glsl */ `
uniform float uA;
void main() { gl_FragColor = vec4(0.0, 0.0, 0.0, uA); }`;

const ringMaterial = (color: string, time: THREE.IUniform): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { uTime: time, uFill: { value: 0 }, uColor: { value: new THREE.Color(color) } },
    vertexShader: LOCAL_VERT,
    fragmentShader: RING_FRAG,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -8,
  });

/** A rope ladder hanging out of the dark above: two ropes and rungs, one merged geometry. */
const ladderGeo = (h: number): THREE.BufferGeometry => {
  const parts: THREE.BufferGeometry[] = [];
  for (const k of [-1, 1]) {
    const r = new THREE.CylinderGeometry(0.018, 0.018, h, 5);
    r.translate(k * 0.17, h / 2, 0);
    parts.push(r);
  }
  for (let y = 0.18; y < h; y += 0.26) {
    const rung = new THREE.CylinderGeometry(0.016, 0.016, 0.36, 5);
    rung.rotateZ(Math.PI / 2);
    rung.translate(0, y, 0);
    parts.push(rung);
  }
  return mergeGeometries(parts) as THREE.BufferGeometry;
};

/** The way out: a shaft of pale light, a rope ladder in it (the deep crypt's exit, and a saga descent's last hall). */
export const exitShaft = (time: THREE.IUniform): THREE.Group => {
  const g = new THREE.Group();
  const shaft = new THREE.Mesh(
    new THREE.CylinderGeometry(0.55, 0.75, 6, 20, 1, true).translate(0, 3, 0),
    new THREE.ShaderMaterial({ uniforms: { uTime: time }, vertexShader: LOCAL_VERT, fragmentShader: SHAFT_FRAG, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, forceSinglePass: true }),
  );
  shaft.renderOrder = 7;
  const ladder = new THREE.Mesh(ladderGeo(5.5), new THREE.MeshStandardMaterial({ color: '#8a7a62', roughness: 1 }));
  ladder.rotation.x = -0.08;
  g.add(shaft, ladder);
  return g;
};

export interface DeepLights {
  hemi: THREE.HemisphereLight | null;
  moon: THREE.DirectionalLight | null;
  rooms: THREE.PointLight[];
}

/** Where the stairs and the way out stand, and how long the party has stood on each (the deep crypt's and the Chasm's). */
export interface DescentState {
  stairs: { q: number; r: number } | null;
  exit: { q: number; r: number } | null;
  stairsT: number;
  exitT: number;
}

/**
 * The way down and the way out once a hall's guardian falls: a stairwell going down (a dark opening, steps into a red
 * glow) and a way out (a shaft of pale light, a rope ladder going up), each with a ring on the floor that fills while
 * the whole party stands on it, and the cut to black of a descent. The deep crypt and the Chasm share it.
 */
export class Descent {
  readonly group = new THREE.Group();
  readonly time = { value: 0 };
  private stairs: THREE.Group;
  private exit: THREE.Group;
  private stairsRing: THREE.Mesh;
  private exitRing: THREE.Mesh;
  private fade: THREE.Mesh;
  private fadeU = { uA: { value: 0 } };
  private fadeAt = -1;
  private stairsKey = '';
  private exitKey = '';
  private stairsT = 0;
  private exitT = 0;
  /** Where the camera should jump on a descent (taken by the screen). */
  private jump: THREE.Vector3 | null = null;

  constructor(
    private ground: (x: number, z: number) => number,
    private glow: GlowLayer,
    private vfx: Vfx,
    private low: boolean,
    /** How near the stairs counts (fixed units) and how long the party stands there to go (ticks). */
    private range: number,
    private need: number,
  ) {
    this.group.name = 'descent';
    // The stairwell: the slot and its steps, a low kerb on three sides.
    this.stairs = new THREE.Group();
    const slot = new THREE.Mesh(
      new THREE.PlaneGeometry(1.15, 1.5).rotateX(-Math.PI / 2),
      new THREE.ShaderMaterial({ uniforms: { uTime: this.time }, vertexShader: LOCAL_VERT, fragmentShader: STAIRS_FRAG, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -8 }),
    );
    slot.position.y = 0.04;
    slot.renderOrder = 1;
    const kerbM = new THREE.MeshStandardMaterial({ color: '#3a3940', roughness: 0.9, flatShading: true });
    const kerbs = [
      new THREE.BoxGeometry(0.14, 0.22, 1.56).translate(-0.62, 0.11, 0),
      new THREE.BoxGeometry(0.14, 0.22, 1.56).translate(0.62, 0.11, 0),
      new THREE.BoxGeometry(1.38, 0.3, 0.16).translate(0, 0.15, -0.8),
      new THREE.BoxGeometry(0.2, 0.55, 0.2).translate(-0.62, 0.27, 0.72),
      new THREE.BoxGeometry(0.2, 0.55, 0.2).translate(0.62, 0.27, 0.72),
    ];
    const kerb = new THREE.Mesh(mergeGeometries(kerbs) as THREE.BufferGeometry, kerbM);
    this.stairs.add(slot, kerb);
    this.stairs.visible = false;
    this.exit = exitShaft(this.time);
    this.exit.visible = false;
    const ringGeo = new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2);
    this.stairsRing = new THREE.Mesh(ringGeo, ringMaterial('#ff4f3a', this.time));
    this.exitRing = new THREE.Mesh(ringGeo, ringMaterial('#dfe8ff', this.time));
    for (const r of [this.stairsRing, this.exitRing]) {
      r.renderOrder = 2;
      r.visible = false;
      r.scale.setScalar((range / 1000) * WORLD_PER_HEX);
    }
    this.fade = new THREE.Mesh(
      new THREE.PlaneGeometry(2, 2),
      new THREE.ShaderMaterial({ uniforms: this.fadeU, vertexShader: FADE_VERT, fragmentShader: FADE_FRAG, transparent: true, depthTest: false, depthWrite: false }),
    );
    this.fade.frustumCulled = false;
    this.fade.renderOrder = 1000;
    this.fade.visible = false;
    this.group.add(this.stairs, this.exit, this.stairsRing, this.exitRing, this.fade);
  }

  /** Every mesh of it, shown for a moment so the scene's compile ahead sees them (they are hidden until a hall falls). */
  parts(): THREE.Object3D[] {
    return [this.stairs, this.exit, this.stairsRing, this.exitRing, this.fade];
  }

  /** Each tick: the stairs and the way out, and how full their rings are. `hall`: the hall they open in (the stairs face away from it). */
  sync(d: DescentState, hall: { q: number; r: number } | null, tick: number, fresh: boolean): void {
    const k = WORLD_PER_HEX / 1000;
    const place = (g: THREE.Object3D, ring: THREE.Mesh, at: { q: number; r: number } | null, key: string, yaw: number): string => {
      const nk = at ? `${at.q},${at.r}` : '';
      if (nk === key) return key;
      g.visible = !!at;
      ring.visible = !!at;
      if (at) {
        const w = hexToWorld(at.q, at.r);
        const x = w.x * k, z = w.y * k;
        const y = this.ground(x, z);
        g.position.set(x, y, z);
        g.rotation.y = yaw;
        ring.position.set(x, y + 0.045, z);
      }
      return nk;
    };
    // The stairs face away from the hall's centre, as if they went down under its wall.
    const yawOf = (at: { q: number; r: number } | null): number => {
      if (!at || !hall) return 0;
      const a = hexToWorld(at.q, at.r);
      const b = hexToWorld(hall.q, hall.r);
      return Math.atan2(a.x - b.x, a.y - b.y) + Math.PI;
    };
    const opened = !this.stairsKey && !!d.stairs;
    this.stairsKey = place(this.stairs, this.stairsRing, d.stairs, this.stairsKey, yawOf(d.stairs));
    this.exitKey = place(this.exit, this.exitRing, d.exit, this.exitKey, 0);
    if (opened && fresh) this.stairsOpened();
    const need = Math.max(1, this.need);
    const reach = (this.range / 1000) * WORLD_PER_HEX;
    ((this.stairsRing.material as THREE.ShaderMaterial).uniforms.uFill as THREE.IUniform).value = clamp01(d.stairsT / need);
    ((this.exitRing.material as THREE.ShaderMaterial).uniforms.uFill as THREE.IUniform).value = clamp01(d.exitT / need);
    // Light: a red glow climbing out of the stairs, pale light pooled under the shaft.
    if (d.stairs) {
      const p = this.stairs.position;
      const beat = 0.8 + 0.2 * Math.sin(tick * 0.4);
      this.glow.add(p.x, p.y + 0.2, p.z, STAIRS_GLOW, 1.9, 0.32 * beat + 0.2 * clamp01(d.stairsT / need));
      if (fresh && tick % (this.low ? 6 : 3) === 0) this.vfx.burst(p.clone().setY(p.y + 0.05), { n: 1, color: tick % 2 ? '#ff8a6e' : '#ff5a2a', speed: 0.004, up: 0.035, size: 0.08, life: 14, gravity: -0.001, spread: 0.35 });
      if (fresh && d.stairsT > this.stairsT && d.stairsT % 4 === 0) this.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, reach, '#ff8a6e', 6, 0.6);
    }
    if (d.exit) {
      const p = this.exit.position;
      this.glow.add(p.x, p.y + 0.15, p.z, EXIT_GLOW, 1.7, 0.3 + 0.2 * clamp01(d.exitT / need));
      if (fresh && tick % (this.low ? 8 : 4) === 0) this.vfx.burst(p.clone().setY(p.y + 0.3 + (tick % 7) * 0.3), { n: 1, color: '#eef2ff', speed: 0.003, up: 0.012, size: 0.06, life: 20, gravity: -0.0005, spread: 0.35 });
      if (fresh && d.exitT > this.exitT && d.exitT % 4 === 0) this.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, reach, '#eef2ff', 6, 0.5);
    }
    this.stairsT = d.stairsT;
    this.exitT = d.exitT;
  }

  /** The stairs open (the guardian fell): the slab grinds aside in dust, fire breathes up out of it. */
  private stairsOpened(): void {
    const p = this.stairs.position.clone();
    this.vfx.ring(p.clone().setY(p.y + 0.05), 0.3, 2.4, '#ff4f3a', 12, 1);
    this.vfx.burst(p.clone().setY(p.y + 0.2), { n: this.low ? 10 : 22, color: '#5a5450', speed: 0.06, up: 0.05, size: 0.45, life: 18, smoke: true, spread: 0.6 });
    this.vfx.burst(p.clone().setY(p.y + 0.1), { n: this.low ? 8 : 16, color: '#ff8a3d', speed: 0.03, up: 0.1, size: 0.1, life: 14, gravity: 0.004, spread: 0.4 });
    this.vfx.shaft(p.clone().setY(p.y + 0.02), p.clone().setY(p.y + 2.4), '#ff5a2a', 8, 0.45);
  }

  /** The party goes down: a cut to black, and the camera should jump to `to`. */
  descend(to: THREE.Vector3): void {
    this.fadeAt = performance.now() / 1000;
    this.fadeU.uA.value = 1;
    this.fade.visible = true;
    this.jump = to.clone();
  }

  /** Whether the screen is black right now (a descent's cut): what is built meanwhile is never seen half done. */
  get dark(): boolean {
    return this.fadeAt >= 0 && this.fadeU.uA.value >= 1;
  }

  /** The spot the camera should jump to after a descent, once (null otherwise). */
  takeJump(): THREE.Vector3 | null {
    const j = this.jump;
    this.jump = null;
    return j;
  }

  frame(now: number): void {
    this.time.value = now % 3600;
    if (this.fadeAt >= 0) {
      // Black at once, held while the camera crosses to the new floor, then the floor fades in.
      const t = now - this.fadeAt;
      const a = t < 0.55 ? 1 : 1 - (t - 0.55) / 0.7;
      if (a <= 0) {
        this.fadeAt = -1;
        this.fade.visible = false;
      } else this.fadeU.uA.value = a;
    }
  }

  dispose(): void {
    this.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
      }
    });
  }
}

const STAIRS_GLOW = new THREE.Color('#ff4f3a');
const EXIT_GLOW = new THREE.Color('#dfe8ff');

export class DeepCryptLook {
  readonly group = new THREE.Group();
  private u = { tVein: { value: veinTexture() }, uAscTime: { value: 0 }, uFloor: { value: 0 } };
  private floor = -1;
  /** Each floor's triangles in the crypt mesh, [first vertex, count] (see byFloor). */
  private ranges: [number, number][] = [];
  private descent: Descent;

  constructor(
    private scene: THREE.Scene,
    private c: CryptBuild,
    private lights: DeepLights,
    private layout: DungeonLayout,
    ground: (x: number, z: number) => number,
    glow: GlowLayer,
    vfx: Vfx,
    low: boolean,
  ) {
    this.group.name = 'deep-crypt';
    this.descent = new Descent(ground, glow, vfx, low, DEEP_CRYPT.stairsRange, DEEP_CRYPT.chooseTicks);
    this.group.add(this.descent.group);
  }

  /** Dresses every floor of the crypt (call once): slate, embers, obsidian. */
  apply(): void {
    const c = this.c;
    const geo = c.mesh.geometry;
    const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    const fl = geo.getAttribute('aFloor') as THREE.BufferAttribute | undefined;
    if (col && fl) {
      const k = new THREE.Color();
      for (let i = 0; i + 2 < col.count; i += 3) {
        const f = fl.getX(i);
        if (f < 0.5) continue;
        const P = f > 1.5 ? GLASS : CRYPT;
        const top = Math.max(pos.getY(i), pos.getY(i + 1), pos.getY(i + 2));
        const wall = top > 0.3;
        for (let j = i; j < i + 3; j++) {
          const l = lum(col.getX(j), col.getY(j), col.getZ(j));
          if (wall) k.copy(P.wallDark).lerp(P.wallLight, clamp01((l - 0.005) / 0.05));
          else if (pos.getY(j) < -0.03 && top < -0.02) k.copy(P.grout);
          else k.copy(P.floorDark).lerp(P.floorLight, clamp01((l - 0.01) / 0.05));
          col.setXYZ(j, k.r, k.g, k.b);
        }
      }
      col.needsUpdate = true;
    }
    if (fl) this.ranges = byFloor(geo, fl);
    c.mesh.material = deepMaterial(c.mesh.material as THREE.MeshStandardMaterial, this.u);
    // Torches: hot red on the second floor, cold violet-white on the third.
    const hot = new THREE.MeshStandardMaterial({ color: '#ffd2a0', emissive: '#ff3d12', emissiveIntensity: 2.2, transparent: true, opacity: 0.95, depthWrite: false, roughness: 0.5 });
    const cold = new THREE.MeshStandardMaterial({ color: '#f0ecff', emissive: '#9a7aff', emissiveIntensity: 2.0, transparent: true, opacity: 0.95, depthWrite: false, roughness: 0.5 });
    for (const f of c.flames) {
      const floor = c.floors[f.room] ?? 0;
      const hall = this.layout.rooms[f.room]?.kind === 'boss';
      if (floor === 1) {
        (f.mesh as THREE.Mesh).material = hot;
        f.glow.material.color.set(hall ? '#ff4f3a' : '#ff5a24');
        f.glow.scale.setScalar(1.15);
      } else if (floor === 2) {
        (f.mesh as THREE.Mesh).material = cold;
        f.glow.material.color.set(hall ? '#d9ccff' : '#a58cff');
      }
    }
    // The rune circles: violet for the Warden, fire for the magma lord, pale glass for the colossus.
    for (const circle of c.runes.children) {
      const floor = c.floors[circle.userData.room as number] ?? 0;
      if (floor === 0) continue;
      const m = new THREE.MeshBasicMaterial({ color: floor === 1 ? '#ff4f3a' : '#c9b8ff', transparent: true, opacity: 0.4, blending: THREE.AdditiveBlending, depthWrite: false });
      circle.traverse((o) => {
        if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).material = m;
      });
    }
    this.scene.add(this.group);
    this.setFloor(0);
  }

  /** Cells of a floor (world x, z), for embers and glass dust. */
  spots(floor: number): [number, number][] {
    const k = WORLD_PER_HEX / 1000;
    const L = this.layout;
    const out: [number, number][] = [];
    for (const h of L.floor) {
      let best = 0;
      let bd = Infinity;
      for (let i = 0; i < L.rooms.length; i++) {
        const rm = L.rooms[i]!;
        const dq = h.q - rm.q;
        const dr = h.r - rm.r;
        const d = (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2 - rm.radius;
        if (d < bd) {
          bd = d;
          best = rm.floor ?? 0;
        }
      }
      if (best !== floor) continue;
      const w = hexToWorld(h.q, h.r);
      out.push([w.x * k, w.y * k]);
    }
    return out;
  }

  /** The party is on this floor: only it is drawn, and the murk, the moonlight and the room lights follow it. */
  setFloor(f: number): void {
    if (f === this.floor) return;
    this.floor = f;
    // The other floors vanish: their flagstones, torches, rune circles and portcullises.
    this.u.uFloor.value = f;
    const range = this.ranges[f];
    if (range) this.c.mesh.geometry.setDrawRange(range[0], range[1]);
    const on = (room: number): boolean => (this.c.floors[room] ?? 0) === f;
    for (const fl of this.c.flames) fl.mesh.visible = fl.glow.visible = on(fl.room);
    for (const circle of this.c.runes.children) circle.visible = on(circle.userData.room as number);
    for (const door of this.c.doors) door.bars.visible = door.seal.visible = on(door.i + 1);
    const sky = FLOOR_SKY[Math.max(0, Math.min(2, f))]!;
    this.scene.background = new THREE.Color(sky.bg);
    if (this.scene.fog instanceof THREE.Fog) this.scene.fog.color.set(sky.fog);
    if (this.lights.hemi) {
      this.lights.hemi.color.set(sky.hemi[0]);
      this.lights.hemi.groundColor.set(sky.hemi[1]);
    }
    if (this.lights.moon) this.lights.moon.color.set(sky.moon);
    const rooms = this.layout.rooms.map((rm, i) => ({ rm, i })).filter(({ rm }) => (rm.floor ?? 0) === f);
    this.lights.rooms.forEach((l, j) => {
      const r = rooms[j];
      if (!r) {
        l.intensity = 0;
        return;
      }
      const hall = r.rm.kind === 'boss';
      l.position.copy(this.c.roomLights[r.i] as THREE.Vector3);
      l.color.set(hall ? sky.hall : sky.room);
      l.intensity = hall ? 22 : 14;
      l.distance = hall ? 11 : 8;
    });
  }

  /** The floor in force (-1 before the first sync). */
  get current(): number {
    return this.floor;
  }

  /** Each tick: the floor, the stairs and the way out, and how full their rings are. */
  sync(deep: DeepCrypt | undefined, tick: number, fresh: boolean): void {
    if (!deep) return;
    this.setFloor(deep.floor);
    const hall = this.layout.rooms.find((rm) => rm.kind === 'boss' && (rm.floor ?? 0) === deep.floor) ?? null;
    this.descent.sync(deep, hall, tick, fresh);
  }

  /** The party goes down: a cut to black, and the camera should jump to `to`. */
  descend(to: THREE.Vector3): void {
    this.descent.descend(to);
  }

  /** The spot the camera should jump to after a descent, once (null otherwise). */
  takeJump(): THREE.Vector3 | null {
    return this.descent.takeJump();
  }

  frame(now: number): void {
    this.u.uAscTime.value = now % 3600;
    this.descent.frame(now);
  }

  dispose(): void {
    this.descent.dispose();
  }
}

/** World position of a hex at floor level. */
export const hexAt = (q: number, r: number, y = 0): THREE.Vector3 => {
  const c = hexToWorld(q, r);
  return simToWorld(c.x, c.y, new THREE.Vector3(), y);
};
